import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import DemoViewBanner from '../demo/DemoViewBanner';
import { getActiveSchoolId, isViewingDemoSchool } from '../utils/activeSchool';
import { getApiBaseUrl } from '../apiBase';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/dateFormat';


export default function ApplicationsPage() {
    const { t } = useTranslation(['applications', 'common', 'nav', 'messages', 'errors']);
    const STATUS_CONFIG = useMemo(() => ({
        pending: { label: t('applications:statusPending'), color: '#e65100', bg: '#fff3e0' },
        approved: { label: t('applications:statusApproved'), color: '#2e7d32', bg: '#e8f5e9' },
        rejected: { label: t('applications:statusRejected'), color: '#c62828', bg: '#fce4ec' }
    }), [t]);
    const FILTER_TABS = useMemo(() => [
        { key: 'pending', label: t('applications:tabPending') },
        { key: 'approved', label: t('applications:tabApproved') },
        { key: 'rejected', label: t('applications:tabRejected') },
        { key: 'all', label: t('applications:tabAll') },
    ], [t]);
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending');
    const isDemo = isViewingDemoSchool();

    const token = localStorage.getItem('token');
    const api = axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${token}` }
    });

    useEffect(() => { loadApps(); }, []);

    async function loadApps() {
        setLoading(true);
        try {
            const schoolId = getActiveSchoolId();
            const params = schoolId ? { schoolId } : {};
            const res = await api.get('/application/incoming', { params });
            setApps(res.data?.items ?? res.data);
        } catch { alert(t('applications:loadError')); }
        setLoading(false);
    }

    async function handleApprove(id) {
        if (isDemo) return alert(t('applications:demoApprove'));
        try {
            const res = await api.post(`/application/${id}/approve`, { response: t('applications:statusApproved') });
            if (res.data?.studentCreated) {
                alert(t('applications:approvedMsg'));
            }
            loadApps();
        } catch (err) {
            alert(err.response?.data?.message || t('common:error'));
        }
    }

    async function handleEnsureStudent(id) {
        if (isDemo) return alert(t('applications:demoAction'));
        try {
            const res = await api.post(`/application/${id}/ensure-student`);
            alert(res.data?.message || t('common:success'));
            loadApps();
        } catch (err) {
            alert(err.response?.data?.message || t('common:error'));
        }
    }

    async function handleReject(id) {
        if (isDemo) return alert(t('applications:demoReject'));
        const response = window.prompt(t('applications:rejectPrompt'));
        if (response === null) return;
        try {
            await api.post(`/application/${id}/reject`, { response });
            loadApps();
        } catch (err) {
            alert(err.response?.data?.message || t('common:error'));
        }
    }

    const filtered = apps.filter(a => filter === 'all' || a.status === filter);

    if (loading) return (
        <OwnerPageLayout title={t('applications:title')} maxWidth={860}>
            <div className="sp-loading-page">{t('common:loading')}</div>
        </OwnerPageLayout>
    );

    const pendingCount = apps.filter(a => a.status === 'pending').length;

    return (
        <OwnerPageLayout
            title={t('applications:titleFull')}
            subtitle={t('applications:subtitle', { count: pendingCount })}
            maxWidth={860}
            beforeMain={<DemoViewBanner />}
        >
                <div className="sp-tabs" style={{ marginBottom: 16 }}>
                    {FILTER_TABS.map(f => (
                        <button
                            key={f.key}
                            type="button"
                            className={`sp-tab${filter === f.key ? ' sp-tab--active' : ''}`}
                            onClick={() => setFilter(f.key)}
                        >
                            {f.label}
                            {f.key !== 'all' && (
                                <span style={{ marginLeft: '6px' }}>
                                    ({apps.filter(a => a.status === f.key).length})
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Список заявок */}
                {filtered.length === 0 ? (
                    <div className="sp-panel" style={{ textAlign: 'center', padding: '48px', color: 'var(--sp-gray-400)' }}>
                        <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
                        {t('applications:empty')}
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: '10px' }}>
                        {filtered.map(app => {
                            const conf = STATUS_CONFIG[app.status];
                            return (
                                <div key={app.id} style={{
                                    background: 'white', borderRadius: '14px',
                                    padding: '18px 20px', border: '1px solid var(--sp-gray-100)'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                                <div style={{
                                                    width: '38px', height: '38px', borderRadius: '50%',
                                                    background: '#1A1A1A', color: 'white',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontWeight: 'bold', fontSize: '16px'
                                                }}>
                                                    {app.username?.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: '700', fontSize: '15px' }}>{app.username}</div>
                                                    <div style={{ fontSize: '13px', color: '#888' }}>{app.email}</div>
                                                </div>
                                                <span style={{
                                                    marginLeft: '8px',
                                                    background: app.role === 'coach' ? '#e3f2fd' : '#f3e5f5',
                                                    color: app.role === 'coach' ? '#EB3333' : '#7b1fa2',
                                                    padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '600'
                                                }}>
                                                    {app.role === 'coach' ? t('applications:roleCoach') : t('applications:roleAthlete')}
                                                </span>
                                            </div>
                                            {app.message && (
                                                <div style={{
                                                    background: 'var(--sp-gray-50)', borderRadius: '8px',
                                                    padding: '8px 12px', fontSize: '13px', color: '#555',
                                                    marginTop: '8px'
                                                }}>
                                                    💬 {app.message}
                                                </div>
                                            )}
                                            <div style={{ fontSize: '12px', color: '#aaa', marginTop: '8px' }}>
                                                {formatDate(app.createdAt)}
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginLeft: '16px' }}>
                                            <span style={{
                                                background: conf.bg, color: conf.color,
                                                padding: '4px 12px', borderRadius: '10px',
                                                fontSize: '12px', fontWeight: '700', textAlign: 'center'
                                            }}>
                                                {conf.label}
                                            </span>
                                            {app.status === 'pending' && !isDemo && (
                                                <>
                                                    <button onClick={() => handleApprove(app.id)} style={{
                                                        padding: '8px 16px', background: '#2ecc71', color: 'white',
                                                        border: 'none', borderRadius: '8px', cursor: 'pointer',
                                                        fontSize: '13px', fontWeight: '600'
                                                    }}>
                                                        {t('applications:approve')}
                                                    </button>
                                                    <button onClick={() => handleReject(app.id)} style={{
                                                        padding: '8px 16px', background: '#fce4ec', color: '#c62828',
                                                        border: '1px solid #ffcdd2', borderRadius: '8px',
                                                        cursor: 'pointer', fontSize: '13px', fontWeight: '600'
                                                    }}>
                                                        {t('applications:reject')}
                                                    </button>
                                                </>
                                            )}
                                            {app.status === 'approved' &&
                                                app.role === 'student' &&
                                                !app.hasStudentProfile &&
                                                !isDemo && (
                                                <button onClick={() => handleEnsureStudent(app.id)} style={{
                                                    padding: '8px 16px', background: '#EB3333', color: 'white',
                                                    border: 'none', borderRadius: '8px', cursor: 'pointer',
                                                    fontSize: '13px', fontWeight: '600'
                                                }}>
                                                    {t('applications:createProfile')}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
        </OwnerPageLayout>
    );
}
